import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { ArticleDtoSeasonality, ProductDtoSeasonality, SeasonDto, SkuDtoSeasonality } from '@/ApiClient.ts';
import { type RouteLocationRaw, type Router, useRouter } from 'vue-router';
import { useSeasons } from '@/store/seasons.ts';
import { useUserConfig } from '@/store/user-config.ts';
import { useCustomFieldGroups } from '@/store/custom-field-groups.ts';
import { useUser } from '@/store/user';

// Personal config is stored per user (not per brand) on the backend, so the brand id is
// baked into the key — otherwise one brand's active season would leak into the next brand.
export const activeSeasonConfigKey = (brandId: number | null) => `settings--activeSeason--latest-${brandId}`;
// Key from before the season was stored per brand. Only honored when its season
// actually belongs to the current brand (see calculateActiveSeason).
const legacyActiveSeasonConfigKey = 'settings--activeSeason--latest';
export const activeSeasonQueryKey = 'season';

// The active season where the entity has it, else its latest — the base only when it has no other.
// Seasonalities arrive latest-first with the base last, as both DTOs order them.
export function resolveSeasonality<T extends { id?: number | undefined }>(
  seasonalities: T[],
  activeSeasonId: number | null | undefined
): T | undefined {
  const active = activeSeasonId ? seasonalities.find((s) => s.id === activeSeasonId) : undefined;
  return active ?? seasonalities[0];
}

export const useActiveSeason = defineStore('active-season', () => {
  const activeSeasonId = ref<null | number>(null);
  const initialized = ref(false);

  const router = useRouter();
  watch(activeSeasonId, (season) => setActiveSeason(router, season));

  const activeSeason = computed<SeasonDto | null>(() =>
    activeSeasonId.value ? useSeasons().byId[activeSeasonId.value] : null
  );

  function rewriteRouteLocation(to: RouteLocationRaw): RouteLocationRaw {
    // We have a first `route.push` in SSR before we have seasons loaded. In that case we just want to keep the route exactly as is.
    if (!initialized.value) return to;

    if (typeof to === 'string') {
      const params = new URLSearchParams(to.split('?')[1]);
      if (!activeSeasonId.value) {
        params.delete(activeSeasonQueryKey);
      } else {
        params.set(activeSeasonQueryKey, activeSeasonId.value.toString());
      }

      return params.toString() === '' ? to.split('?')[0] : `${to.split('?')[0]}?${params.toString()}`;
    }

    return {
      ...to,
      query: { ...to.query, [activeSeasonQueryKey]: activeSeasonId.value ?? undefined },
    };
  }

  function calculateActiveSeason(url: string) {
    if (!useUser().fullyLoaded) throw new Error('Must have seasons loaded before we can calculate active season');

    const query = new URLSearchParams(url.split('?')[1]);
    const querySeasonId = Number(query.get(activeSeasonQueryKey));
    if (querySeasonId && useSeasons().byId[querySeasonId]) {
      activeSeasonId.value = querySeasonId;
      initialized.value = true;

      return;
    }

    // otherwise, use the active season from the user config
    const configSeasonId = Number(useUserConfig().getWithDashes(activeSeasonConfigKey(useUser().brandId)));
    if (configSeasonId && useSeasons().byId[configSeasonId]) {
      activeSeasonId.value = configSeasonId;
      initialized.value = true;

      return;
    }

    // One-time migration from when the season was stored globally per user: the legacy
    // value may belong to another brand, so only honor it when the season exists here.
    const legacySeasonId = Number(useUserConfig().getWithDashes(legacyActiveSeasonConfigKey));
    if (legacySeasonId && useSeasons().byId[legacySeasonId]) {
      activeSeasonId.value = legacySeasonId;
      initialized.value = true;

      return;
    }

    // if no active season is found, then no active season is set
    activeSeasonId.value = null;
    initialized.value = true;
  }

  function activeOrLatestSeasonality<T extends { id?: number | undefined }>(seasonalities: T[]): T | undefined {
    return resolveSeasonality(seasonalities, activeSeasonId.value);
  }

  return {
    activeSeasonId,
    activeSeason,
    rewriteRouteLocation,
    calculateActiveSeason,
    activeOrLatestSeasonality,
  };
});

async function setActiveSeason(router: Router, seasonId: number | null) {
  // Check that season belongs to current brand
  if (seasonId && !useSeasons().byId[seasonId]) {
    return;
  }

  // set in router
  await router.replace({
    query: { ...router.currentRoute.value.query, [activeSeasonQueryKey]: seasonId?.toString() },
  });

  // set in user config
  await useUserConfig().set(activeSeasonConfigKey(useUser().brandId), seasonId);
}
